import { Link } from "@tanstack/react-router";
import { type ReactNode, type Ref, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import { adresseImage } from "./coloriage-requetes";
import { statutDe } from "./queries";
import { EcranDErreur } from "./states";

/**
 * Ce que les deux ecrans d'un cahier partagent : le fil d'Ariane, la page introuvable, l'image
 * d'une version, le style des liens qui ont l'air de boutons.
 *
 * `idValide`, `affichageDeLecture` et `RelectureEchouee` sont nes ici et vivent depuis #226 dans
 * `states.tsx`, que le module Histoires partage : un module n'importe jamais un autre module.
 * Ils restent reexportes pour les ecrans du coloriage, qui n'ont pas change.
 */
export { type Affichage, affichageDeLecture, idValide, RelectureEchouee } from "./states";

/** Les liens poses comme des boutons (`Button` du template ne rend qu'un `button`). */
export const LIEN_BOUTON =
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-left text-sm font-medium transition hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
export const LIEN_BOUTON_PLEIN =
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-left text-sm font-medium text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Le fil d'Ariane du module : `Coloriage › cahier › page`. */
export function FilColoriage({
  cahier,
  children,
}: {
  /** Le cahier, en lien : absent sur la page du cahier lui-meme. */
  cahier?: { id: number; titre: string } | undefined;
  /** Le dernier maillon, en texte. */
  children?: ReactNode;
}) {
  const separateur = <span aria-hidden="true"> › </span>;
  return (
    <p className="text-sm text-muted-foreground">
      <Link className="underline-offset-2 hover:underline" to="/coloriage">
        Coloriage
      </Link>
      {cahier === undefined ? null : (
        <>
          {separateur}
          <Link
            className="underline-offset-2 hover:underline"
            params={{ id: String(cahier.id) }}
            to="/coloriage/cahiers/$id"
          >
            {cahier.titre}
          </Link>
        </>
      )}
      {children === undefined || children === null ? null : (
        <>
          {separateur}
          {children}
        </>
      )}
    </p>
  );
}

/**
 * 404 dans le module : le meme ecran que `Introuvable` (`states.tsx`), mais qui parle du
 * coloriage et ramene aux cahiers, pas au portefeuille.
 */
export function IntrouvableColoriage({ quoi }: { quoi: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Rien sous ce nom</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm text-muted-foreground">
        <p>Le coloriage ne connaît pas {quoi}.</p>
        <p>
          <Link className="underline underline-offset-2" to="/coloriage">
            Revenir aux cahiers
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}

/** Les erreurs de lecture : 404 a la facon du module, le reste par `EcranDErreur`. */
export function ErreurColoriage({ error, quoi }: { error: unknown; quoi: string }) {
  if (statutDe(error) === 404) return <IntrouvableColoriage quoi={quoi} />;
  return <EcranDErreur error={error} quoi={quoi} />;
}

/**
 * L'image d'une version. Les dimensions sont posees sur la balise : le navigateur reserve la
 * place avant l'arrivee des octets, et la page ne saute pas. Une image absente (poussee sans
 * ses octets, ou effacee a la cloture d'un cahier) laisse un cadre qui le dit, a la meme
 * taille, plutot que l'icone d'image cassee. L'adresse porte le debut de l'empreinte de la
 * version (`adresseImage`) : le cache immuable ne sert jamais l'image d'une autre.
 */
export function ImageVersion({
  versionId,
  empreinte,
  presente = true,
  largeur,
  hauteur,
  alt,
  paresseuse = false,
  className,
  imageRef,
}: {
  versionId: number;
  /** Le sha256 du fichier de la version, tel que le backend le rend. */
  empreinte: string;
  /** Faux quand le backend dit deja que l'image manque (`image: false`). */
  presente?: boolean;
  largeur: number;
  hauteur: number;
  alt: string;
  paresseuse?: boolean;
  className?: string;
  imageRef?: Ref<HTMLImageElement>;
}) {
  const [cassee, setCassee] = useState(false);
  if (!presente || cassee) {
    return (
      <span
        className={cn(
          "flex w-full items-center justify-center rounded-md border border-dashed border-border bg-muted p-2 text-center text-xs text-muted-foreground",
          className,
        )}
        style={{ aspectRatio: `${largeur} / ${hauteur}` }}
      >
        image pas encore arrivée
      </span>
    );
  }
  return (
    <img
      alt={alt}
      className={cn("block rounded-md border border-border bg-white", className)}
      height={hauteur}
      loading={paresseuse ? "lazy" : undefined}
      onError={() => setCassee(true)}
      ref={imageRef}
      src={adresseImage(versionId, empreinte)}
      width={largeur}
    />
  );
}
